import { MutationTree } from 'vuex';

import {
  ConversationsStateI,
  ConversationI,
  ConversationIntentI,
  TokensUsageI,
} from './state';

// Markers emitted by the agent that should never appear in the UI
const AGENT_MARKER_RE = /^\/\*(PLANNING|ACTION|REASONING|FINAL_ANSWER)\*\/$/;

function getLastIntent(state: ConversationsStateI): ConversationIntentI {
  return state.conversation.intents[state.conversation.intents.length - 1];
}

export const mutations: MutationTree<ConversationsStateI> = {
  initConversation(state, conversation: ConversationI) {
    state.conversation = conversation;
  },

  pushIntent(state, intent: ConversationIntentI) {
    state.conversation.intents.push({ ...intent, chunks: [] });
  },

  setModelVersion(state, modelVersion: string) {
    state.modelVersion = modelVersion;
  },

  clearThinkingText(state) {
    const intent = getLastIntent(state);
    intent.responseMessage = '';
    intent.chunks = [];
  },

  /**
   * Appends a single streaming token to the last intent.
   * Call this once per text part received from the SSE stream.
   * Filters out /*PLANNING*\/, /*ACTION*\/, /*REASONING*\/, /*FINAL_ANSWER*\/ markers.
   */
  appendResponseMessage(state, payload: { responseMessage: string }) {
    const intent = getLastIntent(state);
    const text = payload.responseMessage;

    // Drop pure agent-marker tokens (they arrive as their own parts)
    if (AGENT_MARKER_RE.test(text.trim())) return;

    intent.responseMessage += text;
    intent.chunks.push(text);
  },

  finishIntent(state) {
    getLastIntent(state).done = true;
  },

  setTokensUsage(state, tokensUsage: TokensUsageI) {
    state.tokensUsage = tokensUsage;
  },

  setThoughts(state, thoughts: string) {
    const intent = getLastIntent(state);
    intent.thoughts = thoughts;
  },

  appendThoughts(state, payload: { text: string; isToolStatus?: boolean }) {
    const intent = getLastIntent(state);
    const { text, isToolStatus } = payload;
    if (!text) return;

    const current = intent.thoughts ?? '';

    if (!current || current === 'thinking...') {
      intent.thoughts = text;
      return;
    }

    if (isToolStatus) {
      if (current.trimEnd().endsWith(text)) return;
      intent.thoughts = `${current.trimEnd()}\n\n${text}`;
      return;
    }

    if (/(?:🔍 Using .+?\.\.\.|✓ .+? completed)\s*$/.test(current)) {
      intent.thoughts = `${current.trimEnd()}\n\n${text}`;
      return;
    }

    intent.thoughts = `${current}${text}`;
  },

  updateResponseMessage(state, payload: { responseMessage: string }) {
    const intent = getLastIntent(state);
    intent.responseMessage = payload.responseMessage;
  },
};
